import React from 'react';
import { Link } from 'react-router-dom';
import {
  Twitter,
  Instagram,
  Youtube,
  Mail,
  Phone,
  MapPin,
  Handshake,
  HelpCircle,
  MessageCircle,
  Newspaper,
} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-rooam-gray-dark border-t border-rooam-gray-light">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Column */}
          <div className="space-y-4 lg:col-span-1">
            <Link to="/" className="inline-block">
              <img 
                src="/rooam-logo.png" 
                alt="Rooam Sports Network" 
                className="h-10 w-auto"
              />
            </Link>
            <p className="text-gray-400 text-sm">
              The future of sports. Live streaming, stats, and tools for athletes, coaches, and scouts.
            </p>
            <div className="flex gap-4">
              <a href="https://x.com/rooamsports?s=21" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-rooam-blue transition-colors" aria-label="X (Twitter)">
                <Twitter className="h-5 w-5" />
              </a>
              <a href="https://www.instagram.com/rooamsportsnetwork" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-rooam-blue transition-colors" aria-label="Instagram">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="https://www.youtube.com/@RooamSportsNetwork" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-rooam-red transition-colors" aria-label="YouTube">
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/events" className="text-gray-400 hover:text-rooam-blue transition-colors text-sm">
                  Events
                </Link>
              </li>
              <li>
                <Link to="/athletes" className="text-gray-400 hover:text-rooam-blue transition-colors text-sm">
                  Athletes
                </Link>
              </li>
              <li>
                <Link to="/membership" className="text-gray-400 hover:text-rooam-blue transition-colors text-sm">
                  Membership
                </Link>
              </li>
              <li>
                <Link to="/articles" className="text-gray-400 hover:text-rooam-blue transition-colors text-sm flex items-center gap-2" data-testid="footer-articles-link">
                  <Newspaper className="h-4 w-4" />
                  Articles
                </Link>
              </li>
            </ul>
          </div>

          {/* FAQ & Support */}
          <div>
            <h4 className="text-white font-semibold mb-4">FAQ &amp; Support</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/faq" className="text-gray-400 hover:text-rooam-blue transition-colors text-sm flex items-center gap-2">
                  <HelpCircle className="h-4 w-4" />
                  FAQ
                </Link>
              </li>
              <li>
                <a href="mailto:support@rooamsports.network?subject=Support%20Request" className="text-gray-400 hover:text-rooam-blue transition-colors text-sm flex items-center gap-2">
                  <MessageCircle className="h-4 w-4" />
                  Contact Support
                </a>
              </li>
              <li>
                <a href="mailto:support@rooamsports.network" className="text-gray-400 hover:text-rooam-blue transition-colors text-sm flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  support@rooamsports.network
                </a>
              </li>
            </ul>
          </div>

          {/* Partner With Us */}
          <div>
            <h4 className="text-white font-semibold mb-4">Partner With Us</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/partner" className="text-gray-400 hover:text-rooam-blue transition-colors text-sm flex items-center gap-2">
                  <Handshake className="h-4 w-4" />
                  Become a Partner
                </Link>
              </li>
              <li>
                <Link to="/partner#schools" className="text-gray-400 hover:text-rooam-blue transition-colors text-sm">
                  For Schools
                </Link>
              </li>
              <li>
                <Link to="/partner#creators" className="text-gray-400 hover:text-rooam-blue transition-colors text-sm">
                  For Creators
                </Link>
              </li>
              <li>
                <Link to="/partner#tournaments" className="text-gray-400 hover:text-rooam-blue transition-colors text-sm">
                  For Tournament Directors
                </Link>
              </li>
              <li>
                <Link to="/partner#leagues" className="text-gray-400 hover:text-rooam-blue transition-colors text-sm">
                  For Leagues
                </Link>
              </li>
            </ul>
          </div>

        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-rooam-gray-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm text-center sm:text-left">
              &copy; {currentYear} Rooam Sports Network. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              <Link to="/legal#privacy-policy" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/legal#terms-of-use" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">
                Terms of Service
              </Link>
              <Link to="/faq" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">
                FAQ
              </Link>
              <Link to="/partner" className="text-rooam-blue hover:text-rooam-blue-light text-sm transition-colors font-medium">
                Partner With Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
